import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { openRazorpayCheckout } from "@/lib/payments";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Lock, Globe } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth()+1).padStart(2,"0");
  const day = String(d.getDate()).padStart(2,"0");
  return `${y}-${m}-${day}`;
}

export default function Book() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [selectedCounselor, setSelectedCounselor] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedSvc, setSelectedSvc] = useState(null);
  const [date, setDate] = useState(undefined);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [busy, setBusy] = useState(false);

  const [bookedFor, setBookedFor] = useState("self");
  const [notes, setNotes] = useState("");
  const [language, setLanguage] = useState("en");
  const [pendingBooking, setPendingBooking] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponStatus, setCouponStatus] = useState(null); // {valid, message}
  const [couponChecking, setCouponChecking] = useState(false);
  const nav = useNavigate();

  // Booker context comes from the user's profile (chosen at registration).
  useEffect(() => {
    if (user?.booking_for) setBookedFor(user.booking_for);
  }, [user]);

  useEffect(() => {
    api.get("/counselors").then(r => {
      setTeam(r.data);
      if (r.data.length) setSelectedCounselor(r.data[0]);
    });
    api.get("/services").then(r => {
      setServices(r.data);
      if (r.data.length) setSelectedSvc(r.data[0]);
    });
  }, []);

  useEffect(() => {
    if (!date || !selectedSvc || !selectedCounselor) { setSlots([]); return; }
    setLoadingSlots(true); setSlot(null);
    api.get("/availability/slots", {
      params: {
        date: fmtDate(date),
        service_id: selectedSvc.id,
        counselor_id: selectedCounselor.id,
      }
    })
      .then(r => setSlots(r.data.slots))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [date, selectedSvc, selectedCounselor]);

  const guardianValid = true;

  const startCheckout = async (booking) => {
    const description = `${booking.service_name} · ${booking.date} ${booking.start_time}`;
    try {
      const result = await openRazorpayCheckout({
        paymentType: "appointment",
        referenceId: booking.id,
        description,
        prefill: { name: user?.name, email: user?.email },
      });
      if (result.status === "success") {
        toast.success("Payment verified — your session is confirmed");
        nav("/dashboard");
        return;
      }
      if (result.status === "failed") {
        toast.error(result.error || "Payment failed. You can retry from your dashboard.");
        setPendingBooking(booking);
      } else if (result.status === "dismissed") {
        toast.message("Payment cancelled. You can retry anytime.");
        setPendingBooking(booking);
      }
    } catch (e) {
      toast.error(e.message || "Something went wrong.");
      setPendingBooking(booking);
    } finally {
      setBusy(false);
    }
  };

  const proceed = async () => {
    if (!selectedCounselor || !selectedSvc || !date || !slot || !guardianValid) return;
    setBusy(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        counselor_id: selectedCounselor.id,
        service_id: selectedSvc.id,
        date: fmtDate(date),
        start_time: slot,
        booked_for: bookedFor === "other" ? "guardian" : bookedFor,
        student_name: bookedFor === "other" ? (user?.name || null) : null,
        student_age: bookedFor === "other" ? (user?.client_age || null) : null,
        language,
        notes: notes.trim() || null,
        coupon_code: couponCode.trim() || null,
      });
      if (booking.payment_status === "paid") {
        toast.success("Coupon applied — your session is confirmed, free of charge");
        nav("/dashboard");
        return;
      }
      await startCheckout(booking);
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const checkCoupon = async () => {
    if (!couponCode.trim() || !selectedSvc) return;
    setCouponChecking(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: couponCode.trim(), service_id: selectedSvc.id });
      setCouponStatus({ valid: true, message: `Coupon applied — ${data.service_name} is free!` });
    } catch (err) {
      setCouponStatus({ valid: false, message: formatApiError(err) });
    } finally {
      setCouponChecking(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <span className="label-eyebrow">Booking</span>
      <h1 className="font-serif-display text-4xl sm:text-5xl mt-2 mb-8">Pour a cup. Reserve your session.</h1>

      {/* Step 1 - Counselor */}
      <div className="card-soft p-8 mb-8">
        <div className="flex items-baseline gap-3 mb-6">
          <span className="chip">Step 1</span>
          <h2 className="font-serif-display text-2xl">Choose your counselor</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="counselor-picker">
          {team.map(c => {
            const isSel = selectedCounselor?.id === c.id;
            return (
              <button key={c.id} data-testid={`pick-counselor-${c.id}`}
                onClick={() => setSelectedCounselor(c)}
                className="text-left rounded-2xl p-5 transition-all flex gap-4"
                style={{
                  border: isSel ? "2px solid var(--brand)" : "1px solid var(--border-soft)",
                  background: isSel ? "rgba(91,117,83,0.05)" : "#fff",
                }}>
                <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0"
                     style={{background:"var(--bg-alt)"}}>
                  {c.photo_url
                    ? <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center font-serif-display text-xl" style={{color:"var(--brand)"}}>{c.name.slice(0,1)}</div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-serif-display text-lg truncate">{c.name}</div>
                  </div>
                  <div className="text-xs mt-1" style={{color:"var(--text-secondary)"}}>{c.title}</div>
                  <div className="text-xs mt-1" style={{color:"var(--brand)"}}>{c.specialty}</div>
                </div>
                {isSel && <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{color:"var(--brand)"}}/>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 2 - Session language */}
      <div className="card-soft p-8 mb-8">
        <div className="flex items-baseline gap-3 mb-6">
          <span className="chip">Step 2</span>
          <h2 className="font-serif-display text-2xl">Preferred session language</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-3" data-testid="lang-picker">
          {[
            {key:"en", title:"English", hint:"Sessions in English."},
            {key:"hi", title:"हिन्दी (Hindi)", hint:"Sessions in Hindi."},
            {key:"both", title:"Bilingual", hint:"Mix of English & Hindi."},
          ].map(opt => {
            const isSel = language === opt.key;
            return (
              <button key={opt.key} type="button"
                data-testid={`lang-${opt.key}`}
                onClick={()=>setLanguage(opt.key)}
                className="text-left rounded-xl px-4 py-3 transition-all flex items-start gap-3"
                style={{
                  border: isSel ? "2px solid var(--brand)" : "1px solid var(--border-soft)",
                  background: isSel ? "rgba(91,117,83,0.06)" : "#fff",
                }}>
                <Globe className="w-4 h-4 mt-0.5" style={{color:"var(--brand)"}}/>
                <div>
                  <div className="text-sm font-medium">{opt.title}</div>
                  <div className="text-xs mt-0.5" style={{color:"var(--text-secondary)"}}>{opt.hint}</div>
                </div>
              </button>
            );
          })}
        </div>
        {bookedFor === "other" && (
          <div className="mt-4 rounded-lg p-3 text-xs" data-testid="other-booker-notice"
               style={{background:"rgba(91,117,83,0.06)", border:"1px solid var(--border-soft)", color:"var(--text-secondary)"}}>
            You're booking on behalf of {user?.client_relationship ? `your ${user.client_relationship.toLowerCase()}` : "someone else"}
            {user?.client_age ? ` (age ${user.client_age})` : ""}. The counsellor will speak with the client during the session.
          </div>
        )}
        <div className="mt-4">
          <label className="label-eyebrow block mb-2">Anything the counselor should know (optional)</label>
          <textarea data-testid="booking-notes" value={notes} onChange={e=>setNotes(e.target.value)} rows={2}
            placeholder="A brief context — stress, relationships, sleep, etc."
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)]"/>
        </div>
      </div>

      {/* Step 3 - Service */}
      <div className="card-soft p-8 mb-8">
        <div className="flex items-baseline gap-3 mb-6">
          <span className="chip">Step 3</span>
          <h2 className="font-serif-display text-2xl">Choose a service</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="book-services">
          {services.map(s => {
            const isSel = selectedSvc?.id === s.id;
            const isHi = !!s.highlight;
            return (
              <button key={s.id} data-testid={`svc-${s.id}`} onClick={() => setSelectedSvc(s)}
                className="text-left rounded-2xl p-5 transition-all relative"
                style={{
                  border: isSel ? "2px solid var(--brand)" : (isHi ? "2px solid rgba(230,178,58,0.55)" : "1px solid var(--border-soft)"),
                  background: isSel ? "rgba(91,117,83,0.05)" : (isHi ? "linear-gradient(180deg, rgba(91,117,83,0.05) 0%, #fff 60%)" : "#fff"),
                }}>
                {isHi && (
                  <span className="absolute -top-2.5 left-4 px-2 py-0.5 rounded-full text-[0.6rem] uppercase tracking-widest font-semibold"
                    style={{background:"var(--accent)", color:"var(--text-primary)"}}>
                    {s.badge || "Recommended"}
                  </span>
                )}
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-serif-display text-lg">{s.name}</div>
                    {s.subtitle && <div className="text-[0.7rem] mt-0.5 font-semibold" style={{color:"var(--brand)"}}>{s.subtitle}</div>}
                  </div>
                  {isSel && <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{color:"var(--brand)"}}/>}
                </div>
                <div className="mt-2 text-xs" style={{color:"var(--text-secondary)"}}>
                  {s.duration_min} min · {s.discount_percent > 0 ? (
                    <>
                      <span className="line-through mr-1">₹{Math.round(s.original_price)}</span>
                      <span style={{color:"var(--brand)"}}>₹{Math.round(s.price)} ({s.discount_percent}% off)</span>
                    </>
                  ) : <>₹{Math.round(s.price)}</>}
                </div>
                <div className="mt-2 text-xs" style={{color:"var(--text-secondary)"}}>{s.description}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 4 - Date + Time */}
      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        <div className="card-soft p-8">
          <div className="flex items-baseline gap-3 mb-6">
            <span className="chip">Step 4</span>
            <h2 className="font-serif-display text-2xl">Pick a date</h2>
          </div>
          <div className="flex justify-center" data-testid="book-calendar">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={{ before: new Date() }}
              className="rounded-2xl"
            />
          </div>
        </div>

        <div className="card-soft p-8">
          <div className="flex items-baseline gap-3 mb-6">
            <span className="chip">Step 5</span>
            <h2 className="font-serif-display text-2xl">Available times</h2>
          </div>
          {!date && <p style={{color:"var(--text-secondary)"}}>Pick a date first to see available time slots.</p>}
          {date && loadingSlots && <p style={{color:"var(--text-secondary)"}}>Finding open slots…</p>}
          {date && !loadingSlots && slots.length === 0 && (
            <p data-testid="no-slots" style={{color:"var(--text-secondary)"}}>No open slots on this day. Try another date or another counselor.</p>
          )}
          {slots.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="slots-grid">
              {slots.map(t => (
                <button key={t} data-testid={`slot-${t}`} onClick={() => setSlot(t)}
                  data-selected={slot === t}
                  className="slot-btn">
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Step 6 - Secure Payment */}
      {!couponStatus?.valid && (
      <div className="card-soft p-8 mb-8" data-testid="payment-section">
        <div className="flex items-baseline gap-3 mb-4">
          <span className="chip">Step 6</span>
          <h2 className="font-serif-display text-2xl">Secure Payment</h2>
        </div>
        <div className="rounded-2xl p-5 flex items-center gap-4"
             style={{border: "1px solid var(--border-soft)", background: "rgba(91,117,83,0.04)"}}>
          <ShieldCheck className="w-10 h-10 flex-shrink-0" style={{color:"var(--brand)"}}/>
          <div>
            <div className="font-serif-display text-lg">Pay by UPI, Cards, Net Banking or Wallets</div>
            <div className="text-xs mt-1" style={{color:"var(--text-secondary)"}}>
              <Lock className="inline w-3 h-3 mr-1" style={{color:"var(--brand)"}}/>
              Powered by Razorpay · Payments are encrypted end-to-end. We never store your card details.
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Summary */}
      <div className="card-soft p-8" data-testid="book-summary">
        {selectedSvc && (
          <div className="mb-6 pb-6 border-b border-[color:var(--border-soft)]">
            <label className="label-eyebrow block mb-2">Have a coupon code?</label>
            <div className="flex items-center gap-2 flex-wrap">
              <input data-testid="coupon-input" value={couponCode}
                onChange={e => { setCouponCode(e.target.value); setCouponStatus(null); }}
                placeholder="e.g. FREE-AB12CD"
                className="rounded-xl border border-[color:var(--border-soft)] px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--brand)]" />
              <button type="button" data-testid="coupon-apply" onClick={checkCoupon}
                disabled={!couponCode.trim() || couponChecking} className="btn-outline text-sm disabled:opacity-40">
                {couponChecking ? "Checking…" : "Apply"}
              </button>
            </div>
            {couponStatus && (
              <div data-testid="coupon-status" className="text-xs mt-2" style={{ color: couponStatus.valid ? "var(--brand)" : "var(--accent)" }}>
                {couponStatus.message}
              </div>
            )}
          </div>
        )}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div>
          <div className="label-eyebrow">Summary</div>
          <div className="font-serif-display text-2xl mt-1">
            {selectedSvc ? selectedSvc.name : "Choose a service"}
            {selectedCounselor ? <span className="text-base font-sans-body ml-2" style={{color:"var(--text-secondary)"}}>with {selectedCounselor.name}</span> : null}
          </div>
          <div className="text-sm mt-1" style={{color:"var(--text-secondary)"}}>
            {date ? date.toDateString() : "No date"} {slot ? `· ${slot}` : ""}
            {bookedFor === "other" && user?.client_relationship ? ` · For your ${user.client_relationship.toLowerCase()}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="label-eyebrow">Total</div>
            <div className="font-serif-display text-3xl">
              {!selectedSvc ? "—" : couponStatus?.valid ? "₹0" : `₹${Math.round(selectedSvc.price)}`}
            </div>
          </div>
          {pendingBooking ? (
            <button data-testid="retry-payment" disabled={busy}
                    onClick={() => { setBusy(true); startCheckout(pendingBooking); }}
                    className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
              {busy ? "Opening…" : "Retry payment"}
            </button>
          ) : (
            <button data-testid="pay-and-book" disabled={!selectedCounselor || !selectedSvc || !date || !slot || !guardianValid || busy}
                    onClick={proceed}
                    className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
              {busy ? "Opening secure payment…" : (couponStatus?.valid ? "Confirm free booking" : "Complete Payment")}
            </button>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
